import { ProgressBar } from 'react-bootstrap';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Neutral600 } from '../../utils/colors';
import { NAVS, TabKey } from './customer-insights-constants';

interface CustomerInsightsHeaderProps {
  tab: TabKey;
  matchRate?: { matchedPopulation: number; totalPopulation: number; fraction: number };
}

export const CustomerInsightsHeader = ({ tab, matchRate }: CustomerInsightsHeaderProps) => {
  const { matchedPopulation, totalPopulation, fraction } = matchRate ?? {
    matchedPopulation: 0,
    totalPopulation: 0,
    fraction: 0
  };

  const prettyLabel = NAVS.find(nav => nav.key === tab)?.label ?? '';

  const aboutCopy: string = (() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return 'Customer Base describes the composition of your customers by observable characteristics such as their age, household income, place of residence, etc.';
      case TabKey.INDEX:
        return 'Index shows the relative percentage of your customer base belonging to each group, as compared to the percentage of the total U.S. market belonging to that group.';
      case TabKey.PENETRATION:
        return 'Penetration shows the percentage of the total U.S. market that is represented in your customer base for each group.';
      default:
        return '';
    }
  })();

  return (
    <div className='customer-insights-header'>
      <div className='header-item about'>
        <div className='header-title'>About {prettyLabel}</div>
        <div className='header-body'>{aboutCopy}</div>
      </div>
      <div className='header-item email-match-rate' data-testid='email-match-rate-section'>
        <div className='header-title'>
          Email Match Rate
          <InfoTooltip iconColor={Neutral600}>
            This is the percentage of distinct customer email addresses that have been successfully
            recognized by the TransUnion graph, which contains a little over 800 million U.S. email
            addresses in total. For large lists of consumer email addresses, resolution rates
            typically hover around 50% - if your rate is much below 50%, it is a good idea to review
            hashing protocol. Your Tinuiti team can share specifics, but all emails should have
            white spaces and special characters removed, they should be lower-cased, and the SHA-256
            hashing algorithm should be applied.
          </InfoTooltip>
        </div>
        <div className='header-body'>
          <div className='progress-bar-label'>
            <div className='line-item'>
              <span>{formatNumber(totalPopulation)} unique emails ingested</span>
              <InfoTooltip size={Size.SM} iconColor={Neutral600}>
                This is the full set of customer email addresses observed at least once within the
                past twelve months.
              </InfoTooltip>
            </div>
            <div className='line-item'>{formatNumber(matchedPopulation)} emails recognized</div>
          </div>
          <div className='progress-bar-container'>
            <ProgressBar
              now={Math.round(fraction * 10000) / 100}
              label={`${Math.round(fraction * 10000) / 100}%`}
              className='progress-bar-wrapper'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
